import React, { useState, useEffect, useCallback } from 'react';
import { AssignedFeature, Feature, Plan, PlanWithFeatures, FeatureAssignmentSummary } from '../types';
import featureService from '../services/featureService';
import Modal from './Modal';

interface PlanFeatureAssignmentProps {
  plan: Plan;
  onClose: () => void;
}

const PlanFeatureAssignment: React.FC<PlanFeatureAssignmentProps> = ({ plan, onClose }) => {
  const [planWithFeatures, setPlanWithFeatures] = useState<PlanWithFeatures | null>(null);
  const [availableFeatures, setAvailableFeatures] = useState<Feature[]>([]);
  const [summary, setSummary] = useState<FeatureAssignmentSummary | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);
  const [selectedAssignedFeatures, setSelectedAssignedFeatures] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'assigned' | 'available'>('assigned');
  const [trialPeriodDrafts, setTrialPeriodDrafts] = useState<Record<number, number>>({});
  const [planHighlightDrafts, setPlanHighlightDrafts] = useState<Record<number, boolean>>({});
  const [numericLimitDrafts, setNumericLimitDrafts] = useState<Record<number, number | null>>({});
  const [savingFeatureId, setSavingFeatureId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [planData, availableData, summaryData, categoriesData] = await Promise.all([
        featureService.getPlanWithFeatures(plan.plan_id),
        featureService.getAvailableFeaturesForPlan(plan.plan_id),
        featureService.getPlanFeatureSummary(plan.plan_id),
        featureService.getFeatureCategories()
      ]);

      setPlanWithFeatures(planData);
      setTrialPeriodDrafts(
        Object.fromEntries(
          planData.features.map((feature) => [
            feature.feature_id,
            feature.trial_period_days ?? 0,
          ]),
        ),
      );
      setPlanHighlightDrafts(
        Object.fromEntries(
          planData.features.map((feature) => [
            feature.feature_id,
            Boolean(feature.show_in_plan_highlights),
          ]),
        ),
      );
      setNumericLimitDrafts(
        Object.fromEntries(
          planData.features.map((feature) => [
            feature.feature_id,
            feature.numeric_limit ?? null,
          ]),
        ),
      );
      setAvailableFeatures(availableData);
      setSummary(summaryData);
      setCategories(categoriesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [plan.plan_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssignFeatures = async () => {
    if (selectedFeatures.length === 0) return;

    try {
      setLoading(true);
      await featureService.bulkAssignFeaturesToPlan({
        plan_id: plan.plan_id,
        feature_ids: selectedFeatures
      });
      
      setSelectedFeatures([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign features');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignFeatures = async () => {
    if (selectedAssignedFeatures.length === 0) return;

    try {
      setLoading(true);
      await featureService.bulkUnassignFeaturesFromPlan({
        plan_id: plan.plan_id,
        feature_ids: selectedAssignedFeatures
      });
      
      setSelectedAssignedFeatures([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unassign features');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureToggle = (featureId: number, isAssigned: boolean) => {
    if (isAssigned) {
      setSelectedAssignedFeatures(prev => 
        prev.includes(featureId) 
          ? prev.filter(id => id !== featureId)
          : [...prev, featureId]
      );
    } else {
      setSelectedFeatures(prev => 
        prev.includes(featureId) 
          ? prev.filter(id => id !== featureId)
          : [...prev, featureId]
      );
    }
  };

  const filterFeatures = (features: Feature[]) => {
    return features.filter(feature => {
      const featureGroupKey = feature.feature_group?.key || feature.category || '';
      const matchesCategory = !filterCategory || featureGroupKey === filterCategory;
      const matchesSearch = !searchTerm || 
        feature.feature_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (feature.comparison_key ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
  };

  const filterAssignedFeatures = (features: AssignedFeature[]) => {
    return features.filter(feature => {
      const featureGroupKey = feature.feature_group_key || feature.category || '';
      const matchesCategory = !filterCategory || featureGroupKey === filterCategory;
      const matchesSearch = !searchTerm || 
        feature.feature_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (feature.comparison_key ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        feature.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
  };

  const handleTrialPeriodChange = (featureId: number, value: string) => {
    const parsedValue = Number(value);
    setTrialPeriodDrafts(prev => ({
      ...prev,
      [featureId]: Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0,
    }));
  };

  const handleNumericLimitChange = (featureId: number, value: string) => {
    const parsedValue = value === '' ? null : Number(value);
    setNumericLimitDrafts(prev => ({
      ...prev,
      [featureId]: Number.isFinite(parsedValue) && parsedValue !== null && parsedValue >= 0 ? parsedValue : null,
    }));
  };

  const handlePlanHighlightChange = (featureId: number, value: boolean) => {
    setPlanHighlightDrafts(prev => ({
      ...prev,
      [featureId]: value,
    }));
  };

  const handleSaveAssignmentMetadata = async (feature: AssignedFeature) => {
    const trialPeriodDays = trialPeriodDrafts[feature.feature_id] ?? 0;
    const showInPlanHighlights =
      planHighlightDrafts[feature.feature_id] ?? feature.show_in_plan_highlights ?? false;
    const numericLimit = numericLimitDrafts[feature.feature_id] ?? null;
    if (
      trialPeriodDays === feature.trial_period_days &&
      showInPlanHighlights === feature.show_in_plan_highlights &&
      numericLimit === feature.numeric_limit
    ) {
      return;
    }

    try {
      setSavingFeatureId(feature.feature_id);
      setError(null);
      await featureService.updatePlanFeatureAssignment(plan.plan_id, feature.feature_id, {
        show_in_plan_highlights: showInPlanHighlights,
        trial_period_days: trialPeriodDays,
        numeric_limit: numericLimit,
      });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update feature assignment');
    } finally {
      setSavingFeatureId(null);
    }
  };

  const renderFeatureCard = (feature: Feature, isAssigned: boolean) => {
    const isSelected = isAssigned 
      ? selectedAssignedFeatures.includes(feature.feature_id)
      : selectedFeatures.includes(feature.feature_id);

    return (
      <div 
        key={feature.feature_id}
        className={`feature-card ${isSelected ? 'selected' : ''} ${isAssigned ? 'assigned' : 'available'}`}
        onClick={() => handleFeatureToggle(feature.feature_id, isAssigned)}
      >
        <div className="feature-header">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleFeatureToggle(feature.feature_id, isAssigned)}
            onClick={(e) => e.stopPropagation()}
          />
          <h4 className="feature-name">
            {feature.display_name || feature.feature_name}
          </h4>
          {(feature.feature_group?.label || feature.category_display_name || feature.category) && (
            <span className="feature-category">
              {feature.feature_group?.label || feature.category_display_name || feature.category}
            </span>
          )}
        </div>
        <div className="feature-details">
          <p className="feature-id">Key: {feature.comparison_key || feature.feature_name}</p>
          {feature.description && (
            <p className="feature-description">{feature.description}</p>
          )}
        </div>
      </div>
    );
  };

  const renderAssignedFeatureCard = (feature: AssignedFeature) => {
    const isSelected = selectedAssignedFeatures.includes(feature.feature_id);
    const trialPeriodDays =
      trialPeriodDrafts[feature.feature_id] ?? feature.trial_period_days ?? 0;
    const showInPlanHighlights =
      planHighlightDrafts[feature.feature_id] ?? feature.show_in_plan_highlights ?? false;
    const numericLimit =
      numericLimitDrafts[feature.feature_id] ?? feature.numeric_limit ?? null;
    const isSaving = savingFeatureId === feature.feature_id;
    const hasChanges =
      trialPeriodDays !== feature.trial_period_days ||
      showInPlanHighlights !== feature.show_in_plan_highlights ||
      numericLimit !== feature.numeric_limit;

    return (
      <div 
        key={feature.feature_id}
        className={`feature-card ${isSelected ? 'selected' : ''} assigned`}
        onClick={() => handleFeatureToggle(feature.feature_id, true)}
      >
        <div className="feature-header">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleFeatureToggle(feature.feature_id, true)}
            onClick={(e) => e.stopPropagation()}
          />
          <h4 className="feature-name">
            {feature.display_name || feature.feature_name}
          </h4>
          {feature.is_new && <span className="new-flag">New</span>}
          {(feature.feature_group_label || feature.category_display_name || feature.category) && (
            <span className="feature-category">
              {feature.feature_group_label || feature.category_display_name || feature.category}
            </span>
          )}
        </div>
        <div className="feature-details">
          <p className="feature-id">Key: {feature.comparison_key || feature.feature_name}</p>
          {feature.description && (
            <p className="feature-description">{feature.description}</p>
          )}
          <div
            className="assignment-metadata-editor"
            onClick={(event) => event.stopPropagation()}
          >
            <label className="highlight-toggle">
              <input
                type="checkbox"
                checked={showInPlanHighlights}
                onChange={(event) =>
                  handlePlanHighlightChange(feature.feature_id, event.target.checked)
                }
              />
              <span>Show in plan highlights</span>
            </label>
            <label className="trial-period-field">
              <span>Trial Period (Days)</span>
              <input
                type="number"
                min="0"
                value={trialPeriodDays}
                onChange={(event) => handleTrialPeriodChange(feature.feature_id, event.target.value)}
                className="trial-period-input"
              />
            </label>
            <label className="numeric-limit-field">
              <span>Numeric Limit</span>
              <input
                type="number"
                min="0"
                value={numericLimit ?? ''}
                onChange={(event) => handleNumericLimitChange(feature.feature_id, event.target.value)}
                className="numeric-limit-input"
                placeholder="No limit"
              />
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={isSaving || !hasChanges}
              onClick={() => void handleSaveAssignmentMetadata(feature)}
            >
              {isSaving ? 'Saving...' : 'Save Details'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !planWithFeatures) {
    return (
      <Modal title={`Manage Comparison Rows - ${plan.name}`} onClose={onClose}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading comparison rows...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Manage Comparison Rows - ${plan.name}`} onClose={onClose}>
      <div className="plan-feature-assignment">
        {error && (
          <div className="alert alert-error">
            <p>{error}</p>
            <button onClick={() => setError(null)} className="btn btn-sm">x</button>
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div className="feature-summary">
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-value">{summary.assigned_features}</span>
                <span className="stat-label">Assigned</span>
              </div>
              <div className="stat">
                <span className="stat-value">{summary.unassigned_features}</span>
                <span className="stat-label">Available</span>
              </div>
              <div className="stat">
                <span className="stat-value">{summary.total_features}</span>
                <span className="stat-label">Total</span>
              </div>
            </div>
            {summary.feature_categories.length > 0 && (
              <div className="assigned-categories">
                <strong>Assigned Groups:</strong>
                {summary.feature_categories.map(category => (
                  <span key={category} className="category-tag">
                    {category}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search comparison rows..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="category-filter"
            >
              <option value="">All Feature Groups</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'assigned' ? 'active' : ''}`}
            onClick={() => setActiveTab('assigned')}
          >
            Assigned Rows ({planWithFeatures?.features.length || 0})
          </button>
          <button
            className={`tab ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => setActiveTab('available')}
          >
            Available Rows ({availableFeatures.length})
          </button>
        </div>

        {/* Feature Lists */}
        <div className="tab-content">
          {activeTab === 'assigned' && (
            <div className="assigned-features">
              <div className="section-header">
                <h3>Assigned Matrix Rows</h3>
                {selectedAssignedFeatures.length > 0 && (
                  <button
                    onClick={handleUnassignFeatures}
                    className="btn btn-danger"
                    disabled={loading}
                  >
                    Remove Selected ({selectedAssignedFeatures.length})
                  </button>
                )}
              </div>
              <div className="features-grid">
                {planWithFeatures && filterAssignedFeatures(planWithFeatures.features).map(feature => 
                  renderAssignedFeatureCard(feature)
                )}
                {planWithFeatures && filterAssignedFeatures(planWithFeatures.features).length === 0 && (
                  <div className="empty-state">
                    <p>No assigned rows match your filters.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'available' && (
            <div className="available-features">
              <div className="section-header">
                <h3>Available Matrix Rows</h3>
                {selectedFeatures.length > 0 && (
                  <button
                    onClick={handleAssignFeatures}
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    Assign Selected ({selectedFeatures.length})
                  </button>
                )}
              </div>
              <div className="features-grid">
                {filterFeatures(availableFeatures).map(feature => 
                  renderFeatureCard(feature, false)
                )}
                {filterFeatures(availableFeatures).length === 0 && (
                  <div className="empty-state">
                    <p>No available rows match your filters.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .plan-feature-assignment {
          max-height: 80vh;
          overflow-y: auto;
        }

        .feature-summary {
          background: #f8f9fa;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .summary-stats {
          display: flex;
          gap: 2rem;
          margin-bottom: 1rem;
        }

        .stat {
          text-align: center;
        }

        .stat-value {
          display: block;
          font-size: 2rem;
          font-weight: bold;
          color: #007bff;
        }

        .stat-label {
          display: block;
          font-size: 0.875rem;
          color: #6c757d;
        }

        .assigned-categories {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .category-tag {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          align-items: center;
        }

        .filter-group {
          flex: 1;
        }

        .search-input, .category-filter {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .tabs {
          display: flex;
          border-bottom: 1px solid #ddd;
          margin-bottom: 1rem;
        }

        .tab {
          padding: 0.75rem 1rem;
          border: none;
          background: none;
          cursor: pointer;
          border-bottom: 2px solid transparent;
        }

        .tab.active {
          border-bottom-color: #007bff;
          color: #007bff;
          font-weight: 500;
        }

        .section-header {
          display: flex;
          justify-content: between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
          max-height: 400px;
          overflow-y: auto;
        }

        .feature-card {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .feature-card:hover {
          border-color: #007bff;
          box-shadow: 0 2px 4px rgba(0,123,255,0.1);
        }

        .feature-card.selected {
          border-color: #007bff;
          background-color: #f8f9ff;
        }

        .feature-card.assigned {
          border-left: 4px solid #28a745;
        }

        .feature-card.available {
          border-left: 4px solid #6c757d;
        }

        .feature-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .feature-name {
          flex: 1;
          margin: 0;
          font-size: 1rem;
          font-weight: 500;
        }

        .feature-category {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .new-flag {
          display: inline-flex;
          align-items: center;
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          background: #fff3db;
          color: #9a6700;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }

        .feature-details {
          margin-left: 1.5rem;
        }

        .assignment-metadata-editor {
          display: flex;
          align-items: flex-end;
          gap: 0.75rem;
          margin-top: 0.85rem;
          flex-wrap: wrap;
        }

        .highlight-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.85rem;
          color: #495057;
          min-height: 38px;
        }

        .trial-period-field {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          font-size: 0.85rem;
          color: #495057;
        }

        .trial-period-input {
          width: 120px;
          padding: 0.45rem 0.6rem;
          border: 1px solid #ced4da;
          border-radius: 6px;
        }

        .numeric-limit-field {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          font-size: 0.85rem;
          color: #495057;
        }

        .numeric-limit-input {
          width: 120px;
          padding: 0.45rem 0.6rem;
          border: 1px solid #ced4da;
          border-radius: 6px;
        }

        .feature-id {
          font-family: monospace;
          font-size: 0.875rem;
          color: #6c757d;
          margin: 0 0 0.25rem 0;
        }

        .feature-description {
          font-size: 0.875rem;
          color: #495057;
          margin: 0;
        }

        .empty-state {
          grid-column: 1 / -1;
          text-align: center;
          padding: 2rem;
          color: #6c757d;
        }

        .loading-container {
          text-align: center;
          padding: 2rem;
        }

        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        `
      }} />
    </Modal>
  );
};

export default PlanFeatureAssignment;
